import React, { useState, useMemo, useCallback } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@this/shared/ui/feedbacks/modal';
import { Button } from '@this/shared/ui/inputs/button';
import { RadioGroup, Radio } from '@this/shared/ui/inputs/radio';
import { Datepicker } from '@this/shared/ui/inputs/datepicker';
import AutoCompletableInput from '@this/components/shared/autocompletable_input/autocompletable_input';
import moment from '@this/lib/moment';

type TripType = 'one-way' | 'round-trip';

interface FormState {
  tripType: TripType;
  needsHotel: boolean;
  outwardDateTime: Date | null;
  departureLocation: string;
  arrivalLocation: string;
  returnDateTime: Date | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onInsert: (text: string) => void;
}

const initialFormState: FormState = {
  tripType: 'one-way',
  needsHotel: false,
  outwardDateTime: null,
  departureLocation: '',
  arrivalLocation: '',
  returnDateTime: null
};

const TemplateAssistantModal = ({ open, onClose, onInsert }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [form, setForm] = useState<FormState>(initialFormState);

  const handleClose = useCallback(() => {
    setForm(initialFormState);
    onClose();
  }, [onClose]);

  const handleTripTypeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const tripType = event.target.value as TripType;
    setForm(prev => ({
      ...prev,
      tripType,
      returnDateTime: tripType === 'one-way' ? null : prev.returnDateTime
    }));
  }, []);

  const handleHotelChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const needsHotel = event.target.value === 'yes';
    setForm(prev => ({ ...prev, needsHotel }));
  }, []);

  const handleOutwardDateTimeChange = useCallback((date: Date | null) => {
    setForm(prev => ({ ...prev, outwardDateTime: date }));
  }, []);

  const handleReturnDateTimeChange = useCallback((date: Date | null) => {
    setForm(prev => ({ ...prev, returnDateTime: date }));
  }, []);

  const handlePlaceChange = useCallback((field: 'origin' | 'destination', value: string) => {
    if (field === 'origin') {
      setForm(prev => ({ ...prev, departureLocation: value }));
    } else {
      setForm(prev => ({ ...prev, arrivalLocation: value }));
    }
  }, []);

  const formatDateTime = (date: Date): string => {
    return moment(date).format('YYYY年MM月DD日 HH:mm');
  };

  const generateText = useCallback((): string => {
    const lines: string[] = [];

    lines.push(`旅程: ${form.tripType === 'one-way' ? '片道' : '往復'}`);
    lines.push(`ホテル: ${form.needsHotel ? '必要' : '不要'}`);

    if (form.outwardDateTime) {
      lines.push(`往路: ${formatDateTime(form.outwardDateTime)}`);
    }

    if (form.departureLocation) {
      lines.push(`出発地: ${form.departureLocation}`);
    }

    if (form.arrivalLocation) {
      lines.push(`到着地: ${form.arrivalLocation}`);
    }

    if (form.tripType === 'round-trip' && form.returnDateTime) {
      lines.push(`復路: ${formatDateTime(form.returnDateTime)}`);
    }

    return lines.join('\n');
  }, [form]);

  const canInsert =
    form.outwardDateTime !== null &&
    form.departureLocation.trim() !== '' &&
    form.arrivalLocation.trim() !== '' &&
    (form.tripType === 'one-way' || form.returnDateTime !== null);

  const handleInsert = useCallback(() => {
    if (!canInsert) return;
    const text = generateText();
    onInsert(text);
    setForm(initialFormState);
  }, [canInsert, generateText, onInsert]);

  return (
    <Modal open={open} onClose={handleClose} size="medium">
      <ModalHeader>テンプレートアシスタント</ModalHeader>
      <ModalBody>
        <div style={styles.formContainer}>
          <div style={styles.formGroup}>
            <label style={styles.label}>旅程</label>
            <RadioGroup layout="horizontal" value={form.tripType} onChange={handleTripTypeChange}>
              <Radio value="one-way">片道</Radio>
              <Radio value="round-trip">往復</Radio>
            </RadioGroup>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>ホテル</label>
            <RadioGroup layout="horizontal" value={form.needsHotel ? 'yes' : 'no'} onChange={handleHotelChange}>
              <Radio value="no">不要</Radio>
              <Radio value="yes">必要</Radio>
            </RadioGroup>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>往路</label>
            <Datepicker
              type="datetime-local"
              value={form.outwardDateTime ?? undefined}
              onChange={handleOutwardDateTimeChange}
              min={new Date()}
              dateFormat="YYYY/MM/DD (ddd) HH:mm"
              disabledClear
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>出発地</label>
            <div style={styles.inputWrapper}>
              <AutoCompletableInput
                id="origin"
                value={form.departureLocation}
                onChange={handlePlaceChange}
                placeholder="会社名 / ビル名 / 駅名など"
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>到着地</label>
            <div style={styles.inputWrapper}>
              <AutoCompletableInput
                id="destination"
                value={form.arrivalLocation}
                onChange={handlePlaceChange}
                placeholder="会社名 / ビル名 / 駅名など"
              />
            </div>
          </div>

          {form.tripType === 'round-trip' && (
            <div style={styles.formGroup}>
              <label style={styles.label}>復路</label>
              <Datepicker
                type="datetime-local"
                value={form.returnDateTime ?? undefined}
                onChange={handleReturnDateTimeChange}
                min={form.outwardDateTime ?? new Date()}
                dateFormat="YYYY/MM/DD (ddd) HH:mm"
                disabledClear
              />
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="sub" onClick={handleClose}>
          キャンセル
        </Button>
        <Button onClick={handleInsert} disabled={!canInsert}>
          挿入
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const getStyles = (theme: AITTheme) => ({
  formContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: 600 as const,
    color: theme.textColor
  },
  inputWrapper: {
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '4px',
    padding: '8px 12px',
    backgroundColor: theme.whiteColor
  }
});

export default TemplateAssistantModal;
